/**
 * FormTemplateListPage — liste des modèles de formulaire (F2-Forms-1c-i / 9.U.4).
 *
 * Pattern UX commun (calqué sur CompanyListPage) : sticky header + filtres
 * (search + status tabs) + table desktop / cards mobile + états distincts
 * (loading skeleton / empty / no-match / error). Création via dialogue léger.
 *
 * Colonnes limitées à ce que l'API liste (1b-i) fournit réellement — nom, code,
 * statut, langue, tags, mis à jour. L'état de la dernière version (brouillon/
 * publiée) est affiché sur la page détail (GET /:id renvoie `latest_version`) :
 * la projection liste ne le porte pas encore (cf. rapport 1c-i, décision PO).
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Pagination, usePagination } from "../../components/Pagination";
import { SearchInput } from "../../components/SearchInput";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { type FormTemplateStatusFilter, useFormTemplates } from "../../lib/queries/formTemplates";
import { toast } from "../../lib/toast";

import { CreateFormTemplateDialog } from "./_parts/CreateFormTemplateDialog";
import { FormTemplateStatusBadge } from "./_parts/FormTemplateBadges";
import { GenerateFromDocumentDialog } from "./_parts/GenerateFromDocumentDialog";

function formatDate(iso: string, lang: "fr" | "en"): string {
  try {
    return new Intl.DateTimeFormat(`${lang}-CA`, { dateStyle: "medium" }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function FormTemplateListPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<FormTemplateStatusFilter>("active");
  const [createOpen, setCreateOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const createBtnRef = useRef<HTMLButtonElement | null>(null);
  const pagination = usePagination();

  const { data, isLoading, isFetching, error, refetch } = useFormTemplates({
    cursor: pagination.cursor,
    q: q || undefined,
    status,
  });

  const onSearchChange = (next: string) => {
    setQ(next);
    pagination.reset();
  };
  const onStatusChange = (next: FormTemplateStatusFilter) => {
    setStatus(next);
    pagination.reset();
  };

  const closeCreate = () => {
    setCreateOpen(false);
    // Retour du focus au déclencheur (a11y).
    queueMicrotask(() => createBtnRef.current?.focus());
  };

  const itemsCount = data?.items.length ?? 0;
  const hasItems = itemsCount > 0;
  const isInitialLoading = isLoading && !data;
  const hasFilter = q.trim() !== "" || status !== "active";

  return (
    <div className="mx-auto w-full max-w-screen-2xl">
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:top-0 lg:-mx-8 lg:px-8 lg:py-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              {t("form_templates.title")}
            </h1>
            <p className="mt-1 text-sm text-slate-600">{t("form_templates.subtitle")}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* 9.U.3 — génération assistée : sortie TOUJOURS en brouillon revu. */}
            <button
              type="button"
              onClick={() => setGenerateOpen(true)}
              className="focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M4 2h5l3 3v9H4V2z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
                <path d="M9 2v3h3" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              </svg>
              {t("form_templates.actions.generate")}
            </button>
            <button
              data-guide-target="templates"
              ref={createBtnRef}
              type="button"
              onClick={() => setCreateOpen(true)}
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M8 3v10M3 8h10"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
              {t("form_templates.actions.create")}
            </button>
          </div>
        </div>
      </header>

      {/* Filters bar */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="min-w-[16rem] flex-1">
          <SearchInput
            value={q}
            onChange={onSearchChange}
            placeholder={t("form_templates.search_placeholder")}
          />
        </div>
        <div
          role="tablist"
          aria-label={t("form_templates.filters.status")}
          className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm shadow-sm"
        >
          {(["active", "archived"] as FormTemplateStatusFilter[]).map((key) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={status === key}
              onClick={() => onStatusChange(key)}
              className={
                status === key
                  ? "bg-brand-600 focus-visible:ring-brand-500 rounded-md px-3 py-1.5 text-sm font-medium text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  : "focus-visible:ring-brand-500 rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              }
            >
              {t(`form_templates.filters.${key}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {error && !isInitialLoading && (
        <div
          role="alert"
          className="mt-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <span aria-hidden="true" className="mt-0.5">
            ⚠
          </span>
          <div className="flex-1">
            <p className="font-medium">
              {error instanceof ApiException
                ? getApiErrorMessage(error, lang) || t("errors.generic")
                : t("errors.generic")}
            </p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-2 touch-manipulation rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              {t("common.retry")}
            </button>
          </div>
        </div>
      )}

      {isInitialLoading ? (
        <ListSkeleton />
      ) : !hasItems ? (
        hasFilter ? (
          <NoMatchState
            onReset={() => {
              setQ("");
              setStatus("active");
              pagination.reset();
            }}
          />
        ) : (
          <EmptyState onCreate={() => setCreateOpen(true)} />
        )
      ) : (
        <>
          {/* Table desktop ≥ md */}
          <div className="mt-6 hidden overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm md:block">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3">
                    {t("form_templates.columns.name")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("form_templates.columns.code")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("form_templates.columns.language")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("form_templates.columns.tags")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("form_templates.columns.latest_version")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("form_templates.columns.updated")}
                  </th>
                  <th scope="col" className="px-4 py-3">
                    {t("form_templates.columns.status")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.items.map((tpl) => (
                  <tr
                    key={tpl.id}
                    className="transition-colors hover:bg-slate-50 motion-reduce:transition-none"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/formulaires/${tpl.id}`}
                        className="hover:text-brand-700 focus-visible:ring-brand-500 rounded-sm font-medium text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      >
                        {tpl.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
                        {tpl.code}
                      </code>
                    </td>
                    <td className="px-4 py-3 uppercase text-slate-600">{tpl.default_language}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {tpl.tags.length ? (
                        <span className="flex flex-wrap gap-1">
                          {tpl.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
                            >
                              {tag}
                            </span>
                          ))}
                          {tpl.tags.length > 3 && (
                            <span className="text-xs text-slate-400">+{tpl.tags.length - 3}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <LatestVersionCell
                        number={tpl.latest_version_number}
                        state={tpl.latest_version_state}
                      />
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-600">
                      {formatDate(tpl.updated_at, lang)}
                    </td>
                    <td className="px-4 py-3">
                      <FormTemplateStatusBadge status={tpl.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards stack mobile < md */}
          <ul className="mt-6 space-y-3 md:hidden">
            {data?.items.map((tpl) => (
              <li key={tpl.id}>
                <Link
                  to={`/formulaires/${tpl.id}`}
                  className="focus-visible:ring-brand-500 block touch-manipulation rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate text-base font-semibold text-slate-900">
                        {tpl.name}
                      </h2>
                      <p className="truncate text-xs text-slate-500">{tpl.code}</p>
                    </div>
                    <FormTemplateStatusBadge status={tpl.status} />
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <dt className="text-slate-500">{t("form_templates.columns.language")}</dt>
                    <dd className="uppercase text-slate-700">{tpl.default_language}</dd>
                    <dt className="text-slate-500">{t("form_templates.columns.latest_version")}</dt>
                    <dd>
                      <LatestVersionCell
                        number={tpl.latest_version_number}
                        state={tpl.latest_version_state}
                      />
                    </dd>
                    <dt className="text-slate-500">{t("form_templates.columns.updated")}</dt>
                    <dd className="tabular-nums text-slate-700">
                      {formatDate(tpl.updated_at, lang)}
                    </dd>
                  </dl>
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs tabular-nums text-slate-500">
              {t("form_templates.list.page_indicator", {
                page: pagination.pageNumber,
                count: itemsCount,
              })}
            </p>
            <Pagination
              pageNumber={pagination.pageNumber}
              hasPrev={pagination.hasPrev}
              hasNext={!!data?.next_cursor}
              onPrev={pagination.prev}
              onNext={() => pagination.next(data?.next_cursor ?? null)}
              disabled={isFetching}
            />
          </div>
        </>
      )}

      {createOpen && (
        <CreateFormTemplateDialog
          onClose={closeCreate}
          onCreated={(result) => {
            setCreateOpen(false);
            toast.success(t("form_templates.create.success"));
            navigate(`/formulaires/${result.template.id}`);
          }}
        />
      )}

      <GenerateFromDocumentDialog open={generateOpen} onClose={() => setGenerateOpen(false)} />
    </div>
  );
}

/** Cellule « dernière version » : `vN · Brouillon/Publiée`, ou `—` si aucune (legacy). */
function LatestVersionCell({
  number,
  state,
}: {
  number: number | null | undefined;
  state: "draft" | "published" | null | undefined;
}) {
  const { t } = useTranslation();
  // `== null` couvre null ET undefined → fail-safe « — » si la projection ne
  // porte pas l'état (jamais « v » sans numéro).
  if (number == null || state == null) {
    return <span className="text-slate-400">—</span>;
  }
  const published = state === "published";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="tabular-nums text-slate-700">v{number}</span>
      <span
        className={
          published
            ? "bg-brand-50 text-brand-700 inline-flex rounded-full px-2 py-0.5 text-xs font-medium"
            : "inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
        }
      >
        {t(
          published
            ? "form_templates.version_state.published"
            : "form_templates.version_state.draft",
        )}
      </span>
    </span>
  );
}

function ListSkeleton() {
  return (
    <div className="mt-6 hidden overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm md:block">
      <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
        <div className="h-3 w-24 rounded bg-slate-200 motion-safe:animate-pulse motion-reduce:animate-none" />
      </div>
      <div className="divide-y divide-slate-100">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3">
            <div className="h-4 w-40 rounded bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
            <div className="h-3 w-24 rounded bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
            <div className="h-3 w-32 rounded bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
            <div className="ml-auto h-5 w-16 rounded-full bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
      <div
        aria-hidden="true"
        className="bg-brand-50 text-brand-600 grid h-12 w-12 place-items-center rounded-full"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z M14 3v6h6 M8.5 13h7M8.5 17h7"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h2 className="mt-4 text-base font-semibold text-slate-900">
        {t("form_templates.empty_state.title")}
      </h2>
      <p className="mt-1 max-w-md text-sm text-slate-600">
        {t("form_templates.empty_state.subtitle")}
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 mt-4 inline-flex touch-manipulation items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        {t("form_templates.actions.create")}
      </button>
    </div>
  );
}

function NoMatchState({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-amber-200 bg-amber-50/40 px-6 py-12 text-center">
      <h3 className="text-base font-semibold text-slate-900">
        {t("form_templates.no_match.title")}
      </h3>
      <p className="mt-1 max-w-md text-sm text-slate-600">
        {t("form_templates.no_match.subtitle")}
      </p>
      <button
        type="button"
        onClick={onReset}
        className="focus-visible:ring-brand-500 mt-4 inline-flex touch-manipulation items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        {t("form_templates.no_match.reset")}
      </button>
    </div>
  );
}
